/**
 * Money helper for financial calculations on the frontend.
 *
 * JavaScript `Number` is IEEE 754 binary double — it cannot represent
 * 0.1, 0.2, or 0.7 exactly, so naive arithmetic drifts by fractions
 * of a penny. In a financial system, that drift is unacceptable.
 *
 * This module provides:
 *   - `toCents(value)`   — convert any input to an integer cent count
 *                           (exact representation, no float math)
 *   - `fromCents(cents)` — convert an integer cent count back to a Number
 *   - `round2(value)`    — round any input to 2 decimal places (Number)
 *   - `add(...values)`   — add via cent arithmetic
 *   - `sub(a, b)`        — subtract via cent arithmetic
 *   - `mul(a, b)`        — multiply via cent arithmetic
 *   - `div(a, b)`        — divide via cent arithmetic
 *   - `percent(amount, pct)` — (amount * pct) / 100 via cent arithmetic
 *   - `isZero`, `compare`, `isPositive`
 *
 * Internally we always work in **integer cents** to avoid float drift,
 * then convert back to a Number with 2-decimal-place rounding at the
 * boundary. This is the same trick accounting systems have used for
 * decades.
 *
 * All public functions return Numbers (not strings), so they slot
 * directly into existing Vue computed properties and `parseFloat()`
 * call sites without any coercion.
 *
 * @example
 *   import { add, mul, percent, round2 } from '@/utils/money';
 *
 *   const subTotal = add(0.1, 0.2);    // 0.3  (not 0.30000000000000004)
 *   const total   = mul(2.5, 4);        // 10   (not 10.000000000000002)
 *   const vat     = percent(100, 7.5);  // 7.5
 */
const CENTS_PATTERN = /^-?\d+(\.\d+)?$/;
const SCALE = 100;

/**
 * Convert any value to an integer number of cents.
 * Accepts: number, numeric string, null, undefined.
 * Returns 0 for null/undefined/empty/invalid.
 */
export function toCents(value) {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return 0;
    }
    // Round half-up at the cent boundary so that 0.1 + 0.2 lands on
    // 30 cents (rounded), not 30.000000000000004.
    return Math.round(value * SCALE);
  }
  if (typeof value === 'string') {
    const cleaned = value.trim();
    if (cleaned === '' || !CENTS_PATTERN.test(cleaned)) {
      return 0;
    }
    // Parse as float then round — even string inputs go through the
    // same cent-quantization to avoid drift.
    return Math.round(parseFloat(cleaned) * SCALE);
  }
  return 0;
}

/**
 * Convert an integer cent count back to a Number with 2 decimal places.
 * Uses a string round-trip to avoid the IEEE 754 representation of e.g.
 * 0.1 being shown as "0.1" instead of "0.1000000000000000055".
 */
export function fromCents(cents) {
  const sign = cents < 0 ? -1 : 1;
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / SCALE);
  const frac = abs % SCALE;
  // Build "whole.frac" as a string and parse once — this preserves
  // exact 2-decimal display.
  const stringValue = whole + '.' + String(frac).padStart(2, '0');
  return sign * parseFloat(stringValue);
}

/**
 * Round a value to 2 decimal places, returning a Number.
 * Uses the standard "EPSILON trick" to defend against the
 * 0.1 + 0.2 = 0.30000000000000004 case.
 */
export function round2(value) {
  const cents = toCents(value);
  return fromCents(cents);
}

/**
 * Add two or more values exactly, returning a Number with 2dp.
 */
export function add(...values) {
  let cents = 0;
  for (const v of values) {
    cents += toCents(v);
  }
  return fromCents(cents);
}

/**
 * Subtract subsequent values from the first, returning a Number with 2dp.
 */
export function sub(minuend, ...subtrahends) {
  let cents = toCents(minuend);
  for (const v of subtrahends) {
    cents -= toCents(v);
  }
  return fromCents(cents);
}

/**
 * Multiply two or more values exactly, returning a Number with 2dp.
 */
export function mul(...values) {
  if (values.length === 0) {
    return 0;
  }
  let cents = toCents(values[0]);
  for (let i = 1; i < values.length; i++) {
    cents = Math.round((cents * toCents(values[i])) / SCALE);
  }
  return fromCents(cents);
}

/**
 * Divide dividend by divisor, returning a Number with 2dp.
 * Returns 0 if divisor is 0.
 */
export function div(dividend, divisor) {
  const divisorCents = toCents(divisor);
  if (divisorCents === 0) {
    return 0;
  }
  const dividendCents = toCents(dividend);
  // (dividend_cents / 100) / (divisor_cents / 100) = dividend_cents / divisor_cents
  // We want the result in 2dp, so multiply by 100 after integer division.
  return fromCents(Math.round(dividendCents * SCALE / divisorCents));
}

/**
 * Compute percentage: (amount * percent) / 100, rounded to 2dp.
 * This is the "VAT/tax percent of base" pattern used throughout billing.
 */
export function percent(amount, percentValue) {
  return mul(amount, div(percentValue, 100));
}

/**
 * Compare two monetary values. Returns -1, 0, or 1.
 * Useful for balance_type decisions (Dr vs Cr).
 */
export function compare(a, b) {
  const aCents = toCents(a);
  const bCents = toCents(b);
  if (aCents < bCents) return -1;
  if (aCents > bCents) return 1;
  return 0;
}

/**
 * Returns true if value is greater than zero (after scaling).
 */
export function isPositive(value) {
  return toCents(value) > 0;
}

/**
 * Returns true if value is zero (after scaling).
 */
export function isZero(value) {
  return toCents(value) === 0;
}

/**
 * Returns the absolute value, rounded to 2dp.
 */
export function abs(value) {
  return fromCents(Math.abs(toCents(value)));
}

/**
 * Format a money value as a localized string with 2 decimal places.
 * Equivalent to the old `formateNumber(value, { round: true })` but
 * accepts an already-rounded number. Use at the display layer only —
 * do not feed the result back into arithmetic.
 */
export function format(value) {
  return round2(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
